import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Quote } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const placeholderTestimonials = [
  {
    id: "1",
    name: "Sarah Johnson",
    location: "Denver, CO",
    rating: 5,
    content: "Our stay at the mountain cabin was absolutely magical. The attention to detail and the stunning views made it an unforgettable experience. We'll definitely be back!",
  },
  {
    id: "2",
    name: "Michael Chen",
    location: "Austin, TX",
    rating: 5,
    content: "Finding the perfect commercial space for our startup was seamless with Morgan Land Company. They understood our needs and found us the ideal location downtown.",
  },
  {
    id: "3",
    name: "Emily Rodriguez",
    location: "San Diego, CA",
    rating: 5,
    content: "Moving into our new long-term rental felt like coming home from day one. The Morgan Land team made the entire process smooth and stress-free.",
  },
];

export const TestimonialsPreview = React.forwardRef<HTMLElement>((_, ref) => {
  const { data: testimonials } = useQuery({
    queryKey: ["featured-testimonials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("testimonials")
        .select("*")
        .eq("is_featured", true)
        .limit(3);

      if (error) throw error;
      return data;
    },
  });

  const displayTestimonials = testimonials?.length ? testimonials : placeholderTestimonials;

  return (
    <section ref={ref} className="py-20">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
            What Our Clients Say
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Don't just take our word for it. Here's what our valued clients have to say about their experiences.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {displayTestimonials.map((testimonial, index) => (
            <Card 
              key={testimonial.id}
              className="relative bg-card border-border hover:shadow-soft transition-all duration-300 animate-slide-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <CardContent className="p-8">
                <Quote className="h-10 w-10 text-primary/20 mb-4" />
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating || 5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                  ))}
                </div>
                <p className="text-foreground mb-6 leading-relaxed">
                  "{testimonial.content}"
                </p>
                <div>
                  <p className="font-semibold text-foreground">{testimonial.name}</p>
                  <p className="text-sm text-muted-foreground">{testimonial.location}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <Button variant="outline" size="lg" asChild>
            <Link to="/testimonials">Read More Testimonials</Link>
          </Button>
        </div>
      </div>
    </section>
  );
});

TestimonialsPreview.displayName = "TestimonialsPreview";
